import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, TrendingUp, Shield, BarChart3, Check,Factory,
  Droplets,
  Utensils,
  GraduationCap,
  Home,
  Hotel,
  Tractor, DollarSign } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import proto from './images1/Prototype.png';
import rrr from './images1/RRR.png';

const benefits = [
    'Uniform mixing without mechanical agitators',
    'Prevention of scum layer formation',
    'Improved contact between microbes and substrate',
    'Stable digester hydrodynamics',
    'Enhanced mass transfer and process reliability',
    'Higher biogas yield',
    'Faster stabilization',
    'Improved digestate quality'
  ];
// ✅ Framer Motion variants (MUST be above component)
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const item = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0 }
};

const applications = [
  { icon: Factory, label: 'Industrial Complexes' },
  { icon: Droplets, label: 'Commercial Establishments' },
  { icon: Utensils, label: 'Canteens & Food Courts' },
  { icon: GraduationCap, label: 'Educational Institutions' },
  { icon: Home, label: 'Residential Communities' },
  { icon: Hotel, label: 'Hotels & Restaurants' },
  { icon: Tractor, label: 'Integrated Farm Waste/Manures' }
];

export const RRRProto = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="rbhm-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              RRR™ Prototype
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Prototype Biogas plants
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              Prototype Biogas Plant Technology by RENVITEK & RRR-DIGESTER™
            </p>
          </motion.div>
          
        </div>
      </section>
      
      {/* Overview */}
        <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <img
            src={proto}
            alt="Biogas Plant"
            className="w-full max-w-md h-auto rounded-lg shadow-xl"
          />
        </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                Prototype Biogas Plant Technology
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
                <p >
                 RENVITEK Engineers LLP designs and deploys advanced prototype biogas systems for decentralized treatment of organic wet waste generated by hostels, educational institutions, gated communities, canteens, hotels, and similar facilities. These systems enable on-site conversion of food waste and iodegradable organics into renewable biogas energy and tabilized bio-manure, directly at the point of waste generation.
                </p>
                <p>
                  The technology is engineered for decentralized waste-to-energy applications, ensuring reliable performance across variable waste streams while minimizing operational complexity.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* GLC Technology */}
      <section className="py-12 md:py-10 bg-white">
      <div className="max-w-5xl mx-auto px-4">
<motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-semibold text-center text-slate-900 mb-4">
          Advanced Gas–Liquid <br /> Circulation (GLC) Technology
        </h2>

        {/* Subtitle */}
        <p className="text-center text-slate-600 max-w-3xl mx-auto mb-14 leading-relaxed tracking-wide">
          RENVITEK’s proprietary Gas–Liquid Circulation (GLC) technology introduces a
          controlled process in which generated biogas is recirculated and mixed with
          the digester slurry under pressure. This mechanism ensures:
        </p>

        {/* Checklist Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-8">
          {benefits.map((item, index) => (
            <div key={index} className="flex items-start gap-4">
              
              {/* Icon Box */}
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-emerald-900 text-white shrink-0">
                <Check size={18} strokeWidth={3} />
              </div>

              {/* Text */}
              <p className="text-slate-700 text-base leading-relaxed tracking-wide">
                {item}
              </p>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p className="text-sm text-slate-500 mt-14 text-center max-w-4xl mx-auto leading-relaxed tracking-wide">
          Compared to conventional fixed-dome and floating-drum digesters,
          the GLC-based system delivers higher biogas yield, faster stabilization,
          and improved digestate quality, particularly for heterogeneous organic
          waste streams.
        </p>
        </motion.div>
      </div>
    </section>
          <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Sustainable Energy & Institutional Compliance
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Biogas generated through RENVITEK systems serves as a renewable thermal energy source for cooking, hot water generation, and other institutional energy needs. At the same time, the technology supports:</p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Reduction of greenhouse gas emissions</li>
              <li>On-site organic waste management</li>
              <li>Lower waste handling and disposal costs</li>
              <li>Compliance with NAAC green audit and sustainability criteria</li>
              <li>Adoption of measurable environmental best practices</li>
            </ul>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">By integrating biogas plants, institutions can significantly enhance their energy efficiency, carbon footprint management, and environmental stewardship.</p>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Technology Novelty & Process Engineering Strength
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">The RENVITEK prototype biogas technology is designed around high-rate bio-methanation principles, incorporating:</p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Optimized slurry concentration and feed management</li>
              <li>Effective food-to-microorganism (F/M) ratio control</li>
              <li>Retention of highly active anaerobic biomass</li>
              <li>Improved buffering capacity against shock loads</li>
              <li>Mechanisms to minimize microbial inhibition</li>
              <li>Decoupling of Hydraulic Retention Time (HRT) and Solids Retention Time (SRT)</li>
            </ul>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">These design features result in a compact reactor footprint, faster digestion kinetics, and consistent biogas generation even under variable waste characteristics.</p>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Turnkey & Customized Solutions
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">RENVITEK provides end-to-end solutions for solid organic waste-to-energy systems, including:</p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Process design and engineering audits</li>
              <li>Civil, FRP, and mild-steel digester structures</li>
              <li>Fabrication, erection, and commissioning support</li>
              <li>Technology customization based on waste composition and load</li>
              <li>Performance optimization and operational guidance</li>
            </ul>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Each system is engineered to meet site-specific requirements, ensuring long-term operational stability and maximum energy recovery.</p>
          </div>
        </motion.div>
        </div>
      </section>
{/* RRR technology */}
          <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid lg:grid-cols-2 gap-16">
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                RRR-DIGESTER™ (Resource · Recovery · Reuse Digester)
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
                <p>
                 The RRR-DIGESTER™ is RENVITEK’s proprietary next-generation anaerobic digestion system for decentralized organic waste treatment with simultaneous recovery of biogas energy and nutrient-rich digestate. The technology converts waste liabilities into energy, reusable resources, and economic value, fully aligned with circular economy principles.
                </p>
                <p>
                  Resource: Organic waste treated as a valuable input <br></br>
                  Recovery: Efficient recovery of biogas and Digestate <br></br>
                  Reuse: Reuse of energy (biogas) and digestate (bio-manure)
                </p>
              </div>
            </motion.div>
            <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <img
            src={rrr}
            alt="Biogas Plant"
            className="w-full max-w-md h-auto rounded-lg shadow-xl"
          />
        </motion.div>
          </div>
        </div>
      </section>
{/*  */}
          <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
      <div className="max-w-6xl mx-auto px-4 text-center">

        {/* Small badge */}
        <motion.span
          className="inline-block text-sm font-semibold tracking-wide text-slate-500 dark:text-white/60 mb-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Applications
        </motion.span>

        {/* Title */}
        <motion.h2
          className="text-3xl md:text-4xl font-semibold text-slate-900 dark:text-white mb-3"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Where RRR-DIGESTER Works
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          className="text-slate-600 dark:text-white/70 mb-16 leading-relaxed tracking-wide"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Versatile solutions for diverse waste management needs.
        </motion.p>

        {/* Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-y-14 gap-x-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {applications.map((app, index) => (
            <motion.div
              key={index}
              variants={item}
              className="flex flex-col items-center gap-4"
            >
              {/* Icon box */}
              <motion.div
                className="w-14 h-14 rounded-xl bg-emerald-900 text-white flex items-center justify-center"
                whileHover={{ scale: 1.08 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <app.icon size={26} strokeWidth={2} />
              </motion.div>

              {/* Label */}
              <p className="text-sm font-medium text-slate-700 dark:text-white/80 tracking-wide leading-relaxed">
                {app.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
{/*  */}

          <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
               Key Technical Features
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              
                <p>
                  <h4 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">Safety</h4>
                 multi-layer safety architecture including gas control valves, pressure relief systems, flame arrestors, and fully enclosed gas handling. 
                </p>
                <p>
                  <h4 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">Structural Longevity</h4>
                 Digesters fabricated from FRP and/or corrosion-resistant steel, offering 20–25 years of service life with low lifecycle cost. 
                </p>
                <p>
                  <h4 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">Process Efficiency</h4>
                 High-rate digestion with stable operation across variable feedstocks, low maintenance, and optimized organic loading. 
                </p>
                <p>
                  <h4 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">Sustainability Impact</h4>
                 Supports waste reduction at source, renewable energy generation, nutrient recycling, and long term environmental and economic sustainability. 
                </p>
                <p>
                  <h4 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">Digital Monitoring & Smart Operations</h4>
                 RRR-DIGESTER™ is integrated with RENVITEK Biodigester Health Monitoring (RBHM) technology, offering app-based remote monitoring, continuous data analytics, early warning indicators, and transparent performance reporting. 
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
            >
           <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Core Advantages of RRR-DIGESTER™
            </h3>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>High-efficiency anaerobic digester architecture with maximum organic degradation</li>
              <li>Compatible with mixed biodegradable feedstocks including food waste, vegetable waste, starch-rich wastewater, used cooking oil, garden waste, and organic effluents</li>
              <li>Compact, modular, prefabricated design for rapid installation</li>
              <li>Odourless, noiseless operation with minimal maintenance</li>
              <li>Very low parasitic power consumption</li>
              <li>Stable performance under underfeeding and overfeeding conditions</li>
              <li>No scum formation and negligible solid residue</li>
              <li>Low operating expenditure and easy digestate handling</li>
              <li>Specialized anaerobic microbial consortium tolerant to temperature variations</li>
            </ul>
          </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-emerald-950 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Transform Waste into Energy?
          </motion.h2>
          <motion.p
            className="text-emerald-900 text-lg mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Contact us today to discuss how RENVITEK can help you achieve your sustainability goals.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/contact">
              <motion.button
                className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="cta-contact-btn"
              >
                Contact Us Today
                <ArrowRight size={20} />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RRRProto;
